import { useEffect, useMemo, useState } from 'react'
import {
  extractDeviceState,
  queryDevicesWithAuthContext,
  queryDeviceStatusWithAuthContext,
  type QueryDevicesResponse,
  type QueryStatusResponse,
} from '../../services/turingApi'

type Credentials = { apiKey?: string; accessToken?: string; appId?: string } | undefined
type PhaseId = 'r' | 's' | 't'

export type EnergyPhaseSnapshot = {
  power: number | null
  voltage: number | null
  current: number | null
  powerFactor: number | null
  energy: number | null
}

export type EnergyDeviceSnapshot = {
  power: number | null
  voltage: number | null
  current: number | null
  powerFactor: number | null
  frequency: number | null
  energy: number | null
  energyUnit: string | null
  phases: Record<PhaseId, EnergyPhaseSnapshot>
  totalPower: number | null
  totalEnergy: number | null
  totalEnergyUnit: string | null
  updatedAt: number
}

const PHASES: PhaseId[] = ['r', 's', 't']
type MetricUnitMap = Record<string, string>

const UNIT_MAP_CACHE_TTL_MS = 10 * 60 * 1000
const STATUS_CACHE_TTL_MS = 2500
const SNAPSHOT_CACHE_TTL_MS = 30 * 1000

const metricUnitMapCache = new Map<string, { value: MetricUnitMap; expiresAt: number }>()
const metricUnitMapInflight = new Map<string, Promise<MetricUnitMap>>()
const statusCache = new Map<string, { value: QueryStatusResponse; expiresAt: number }>()
const statusInflight = new Map<string, Promise<QueryStatusResponse>>()
const snapshotCache = new Map<string, { value: EnergyDeviceSnapshot; expiresAt: number }>()

const PREVIEW_SNAPSHOT: EnergyDeviceSnapshot = {
  power: 492,
  voltage: 227,
  current: 2.2,
  powerFactor: 0.98,
  frequency: 60.02,
  energy: 15226.1,
  energyUnit: 'kWh',
  phases: {
    r: { power: 492, voltage: 227, current: 2.2, powerFactor: 0.98, energy: 5100.3 },
    s: { power: 274, voltage: 226, current: 1.2, powerFactor: 0.94, energy: 4780.8 },
    t: { power: 368, voltage: 228, current: 1.6, powerFactor: 0.96, energy: 5345.0 },
  },
  totalPower: 1134,
  totalEnergy: 15226.1,
  totalEnergyUnit: 'kWh',
  updatedAt: Date.now(),
}

function toNumber(value: unknown): number | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    return toNumber(value[0])
  }
  if (value && typeof value === 'object') {
    const payload = value as Record<string, unknown>
    if ('value' in payload) return toNumber(payload.value)
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  }
  return null
}

function averageNumbers(values: Array<number | null>): number | null {
  const present = values.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
  if (present.length === 0) return null
  const sum = present.reduce((acc, item) => acc + item, 0)
  return sum / present.length
}

function sumNumbers(values: Array<number | null>): number | null {
  const present = values.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
  if (present.length === 0) return null
  return present.reduce((acc, item) => acc + item, 0)
}

function normalizeMetricKey(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function normalizeUnitToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function convertCurrentByUnit(value: number | null, unit: string | null): number | null {
  if (value === null || !Number.isFinite(value)) return null
  const token = normalizeUnitToken(unit || '')
  if (!token) return value
  // Unit source of truth: QueryDevices.attributes.sensorProfile.<metric>.Unit
  if (token === 'ma' || token === 'mamp' || token.includes('milliamp')) return value / 1000
  return value
}

function pickStateMetric(
  state: Record<string, unknown>,
  keys: string[]
): { value: number; key: string } | null {
  for (const key of keys) {
    const value = toNumber(state[key])
    if (value !== null) return { value, key }
  }
  return null
}

function extractMetricUnitMapFromDevices(
  response: QueryDevicesResponse,
  deviceSN: string
): MetricUnitMap {
  const targetDeviceSN = String(deviceSN || '').trim()
  const targetBaseSN = targetDeviceSN.split('#')[0]
  const devices = Array.isArray(response?.data) ? response.data : []
  let fallbackCapability: Record<string, unknown> | null = null

  for (const device of devices) {
    const capabilities = Array.isArray(device?.capabilities) ? device.capabilities : []
    for (const capability of capabilities) {
      const capRecord = capability as unknown as Record<string, unknown>
      const capSN = String(capRecord.deviceSN || '').trim()
      if (!fallbackCapability && capSN && capSN.split('#')[0] === targetBaseSN) {
        fallbackCapability = capRecord
      }
      if (!targetDeviceSN || capSN !== targetDeviceSN) continue

      const profile = (capRecord.attributes as Record<string, unknown> | undefined)?.sensorProfile
      if (!profile || typeof profile !== 'object' || Array.isArray(profile)) return {}
      const map: MetricUnitMap = {}
      for (const [metricKey, spec] of Object.entries(profile as Record<string, unknown>)) {
        if (!spec || typeof spec !== 'object' || Array.isArray(spec)) continue
        const unit = String((spec as Record<string, unknown>).Unit || '').trim()
        if (!unit) continue
        map[normalizeMetricKey(metricKey)] = unit
      }
      return map
    }
  }

  if (!fallbackCapability) return {}
  const fallbackProfile = (fallbackCapability.attributes as Record<string, unknown> | undefined)?.sensorProfile
  if (!fallbackProfile || typeof fallbackProfile !== 'object' || Array.isArray(fallbackProfile)) return {}
  const fallbackMap: MetricUnitMap = {}
  for (const [metricKey, spec] of Object.entries(fallbackProfile as Record<string, unknown>)) {
    if (!spec || typeof spec !== 'object' || Array.isArray(spec)) continue
    const unit = String((spec as Record<string, unknown>).Unit || '').trim()
    if (!unit) continue
    fallbackMap[normalizeMetricKey(metricKey)] = unit
  }
  return fallbackMap
}

function resolveMetricUnit(
  unitMap: MetricUnitMap,
  keys: string[]
): string | null {
  for (const key of keys) {
    const unit = unitMap[normalizeMetricKey(key)]
    if (unit) return unit
  }
  return null
}

function resolveAuthCacheScope(credentials?: Credentials): string {
  const appId = String(credentials?.appId || '').trim() || 'no-app'
  if (credentials?.apiKey) return `apikey:${appId}`
  if (credentials?.accessToken) return `oauth:${appId}`
  return `anon:${appId}`
}

function buildRealtimeCacheKey(deviceSN: string, credentials?: Credentials): string {
  return `${resolveAuthCacheScope(credentials)}:${String(deviceSN || '').trim()}`
}

function getFreshSnapshotFromCache(cacheKey: string): EnergyDeviceSnapshot | null {
  if (!cacheKey) return null
  const cached = snapshotCache.get(cacheKey)
  if (!cached) return null
  if (cached.expiresAt <= Date.now()) {
    snapshotCache.delete(cacheKey)
    return null
  }
  return cached.value
}

function setSnapshotCache(cacheKey: string, snapshot: EnergyDeviceSnapshot): void {
  if (!cacheKey) return
  snapshotCache.set(cacheKey, {
    value: snapshot,
    expiresAt: Date.now() + SNAPSHOT_CACHE_TTL_MS,
  })
}

function getFreshMetricUnitMap(cacheKey: string): MetricUnitMap | null {
  const cached = metricUnitMapCache.get(cacheKey)
  if (!cached) return null
  if (cached.expiresAt <= Date.now()) {
    metricUnitMapCache.delete(cacheKey)
    return null
  }
  return cached.value
}

async function loadMetricUnitMapCached(
  auth: NonNullable<Credentials>,
  deviceSN: string,
  cacheKey: string
): Promise<MetricUnitMap> {
  const fresh = getFreshMetricUnitMap(cacheKey)
  if (fresh) return fresh

  const inflight = metricUnitMapInflight.get(cacheKey)
  if (inflight) return inflight

  const promise = (async () => {
    try {
      const devicesResponse = await queryDevicesWithAuthContext(auth, [deviceSN])
      const unitMap = extractMetricUnitMapFromDevices(devicesResponse, deviceSN)
      metricUnitMapCache.set(cacheKey, {
        value: unitMap,
        expiresAt: Date.now() + UNIT_MAP_CACHE_TTL_MS,
      })
      return unitMap
    } catch {
      metricUnitMapCache.set(cacheKey, {
        value: {},
        expiresAt: Date.now() + Math.min(UNIT_MAP_CACHE_TTL_MS, 30 * 1000),
      })
      return {}
    } finally {
      metricUnitMapInflight.delete(cacheKey)
    }
  })()

  metricUnitMapInflight.set(cacheKey, promise)
  return promise
}

function getFreshStatus(cacheKey: string): QueryStatusResponse | null {
  const cached = statusCache.get(cacheKey)
  if (!cached) return null
  if (cached.expiresAt <= Date.now()) {
    statusCache.delete(cacheKey)
    return null
  }
  return cached.value
}

async function queryStatusCached(
  auth: NonNullable<Credentials>,
  deviceSN: string,
  cacheKey: string
): Promise<QueryStatusResponse> {
  const fresh = getFreshStatus(cacheKey)
  if (fresh) return fresh

  const inflight = statusInflight.get(cacheKey)
  if (inflight) return inflight

  const promise = (async () => {
    try {
      const response = await queryDeviceStatusWithAuthContext(auth, deviceSN)
      statusCache.set(cacheKey, {
        value: response,
        expiresAt: Date.now() + STATUS_CACHE_TTL_MS,
      })
      return response
    } finally {
      statusInflight.delete(cacheKey)
    }
  })()

  statusInflight.set(cacheKey, promise)
  return promise
}

function pickStateNumber(state: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = toNumber(state[key])
    if (value !== null) return value
  }
  return null
}

function getPhasePower(state: Record<string, unknown>, phase: PhaseId): number | null {
  return pickStateNumber(state, [`real_power_${phase}`, `watt_${phase}`, `power_${phase}`])
}

function getPhaseVoltage(state: Record<string, unknown>, phase: PhaseId): number | null {
  return pickStateNumber(state, [`voltage_${phase}`, `voltage_phase_${phase}`])
}

function getPhaseCurrent(
  state: Record<string, unknown>,
  phase: PhaseId,
  unitMap: MetricUnitMap
): number | null {
  const keys = [`current_${phase}`, `current_phase_${phase}`]
  const metric = pickStateMetric(state, keys)
  if (!metric) return null
  return convertCurrentByUnit(metric.value, resolveMetricUnit(unitMap, [metric.key, ...keys, 'current']))
}

function getPhasePowerFactor(state: Record<string, unknown>, phase: PhaseId): number | null {
  return pickStateNumber(state, [`power_factor_${phase}`, `pf_${phase}`])
}

function getPhaseEnergy(state: Record<string, unknown>, phase: PhaseId): number | null {
  return pickStateNumber(state, [`watt_hour_${phase}`, `energy_${phase}`])
}

function resolveStateFromStatus(response: QueryStatusResponse, deviceSN: string): Record<string, unknown> | null {
  const direct = extractDeviceState(response, deviceSN)
  if (direct) return direct

  const baseSN = String(deviceSN || '').split('#')[0]
  if (!baseSN || !Array.isArray(response?.data)) return null
  for (const device of response.data) {
    for (const state of device?.states || []) {
      if (String(state?.deviceSN || '').split('#')[0] !== baseSN) continue
      if (!state?.values || typeof state.values !== 'object') continue
      const values: Record<string, unknown> = {}
      for (const [key, payload] of Object.entries(state.values)) {
        values[key] = (payload as { value?: unknown })?.value
      }
      return values
    }
  }
  return null
}

function buildSnapshotFromState(
  state: Record<string, unknown>,
  unitMap: MetricUnitMap
): EnergyDeviceSnapshot {
  const phases = Object.fromEntries(
    PHASES.map((phase) => {
      const phasePower = getPhasePower(state, phase)
      const phaseVoltage = getPhaseVoltage(state, phase)
      const phasePowerFactor = getPhasePowerFactor(state, phase)
      const phaseCurrent = getPhaseCurrent(state, phase, unitMap)
      return [
        phase,
        {
          power: phasePower,
          voltage: phaseVoltage,
          current: phaseCurrent,
          powerFactor: phasePowerFactor,
          energy: getPhaseEnergy(state, phase),
        } satisfies EnergyPhaseSnapshot,
      ]
    })
  ) as Record<PhaseId, EnergyPhaseSnapshot>

  const totalPower = pickStateNumber(state, ['real_power_total', 'watt_total', 'real_power', 'watt'])
    ?? sumNumbers(PHASES.map((phase) => phases[phase].power))
  const totalEnergyMetric = pickStateMetric(state, ['watt_hour_total', 'watt_hour', 'energy'])
  const totalEnergy = (totalEnergyMetric?.value ?? null)
    ?? sumNumbers(PHASES.map((phase) => phases[phase].energy))
  const totalEnergyUnit = resolveMetricUnit(
    unitMap,
    totalEnergyMetric
      ? [totalEnergyMetric.key, 'watt_hour_total', 'watt_hour', 'energy']
      : ['watt_hour_total', 'watt_hour', 'energy']
  )

  const voltage = pickStateNumber(state, ['voltage']) ?? averageNumbers(PHASES.map((phase) => phases[phase].voltage))
  const powerFactor = pickStateNumber(state, ['power_factor', 'pf']) ?? averageNumbers(PHASES.map((phase) => phases[phase].powerFactor))
  const frequency = pickStateNumber(state, ['frequency', 'hz'])
  const power = pickStateNumber(state, ['real_power', 'watt']) ?? totalPower
  const energyMetric = pickStateMetric(state, ['watt_hour', 'energy'])
  const energy = (energyMetric?.value ?? null) ?? totalEnergy
  const energyUnit =
    resolveMetricUnit(
      unitMap,
      energyMetric ? [energyMetric.key, 'watt_hour', 'energy'] : ['watt_hour', 'energy']
    ) ?? totalEnergyUnit
  const currentMetric = pickStateMetric(state, ['current'])
  const currentFromState = currentMetric
    ? convertCurrentByUnit(currentMetric.value, resolveMetricUnit(unitMap, [currentMetric.key, 'current']))
    : null
  const current = currentFromState ?? averageNumbers(PHASES.map((phase) => phases[phase].current))
  const updatedAt = Date.now()

  return {
    power,
    voltage,
    current,
    powerFactor,
    frequency,
    energy,
    energyUnit,
    phases,
    totalPower,
    totalEnergy,
    totalEnergyUnit,
    updatedAt,
  }
}

export function getPreviewEnergyDeviceSnapshot(): EnergyDeviceSnapshot {
  return {
    ...PREVIEW_SNAPSHOT,
    phases: {
      r: { ...PREVIEW_SNAPSHOT.phases.r },
      s: { ...PREVIEW_SNAPSHOT.phases.s },
      t: { ...PREVIEW_SNAPSHOT.phases.t },
    },
    updatedAt: Date.now(),
  }
}

export function useEnergyDeviceSnapshot(
  deviceSN?: string | null,
  credentials?: Credentials,
  options?: { pollMs?: number; previewFallback?: boolean }
) {
  const sn = String(deviceSN || '').trim()
  const hasRealtimeSource = Boolean(
    sn && (credentials?.apiKey || credentials?.accessToken)
  )
  const previewFallback = options?.previewFallback !== false
  const realtimeCacheKey = hasRealtimeSource ? buildRealtimeCacheKey(sn, credentials) : ''
  const initialRealtimeSnapshot = hasRealtimeSource ? getFreshSnapshotFromCache(realtimeCacheKey) : null
  const [snapshot, setSnapshot] = useState<EnergyDeviceSnapshot | null>(() =>
    hasRealtimeSource
      ? (initialRealtimeSnapshot ?? null)
      : (previewFallback ? getPreviewEnergyDeviceSnapshot() : null)
  )
  const [loading, setLoading] = useState<boolean>(hasRealtimeSource && !initialRealtimeSnapshot)

  useEffect(() => {
    let cancelled = false
    const snValue = String(deviceSN || '').trim()
    const auth = {
      apiKey: credentials?.apiKey,
      accessToken: credentials?.accessToken,
      appId: credentials?.appId,
    }
    const hasAuth = Boolean(auth.apiKey || auth.accessToken)
    const realtimeKey = buildRealtimeCacheKey(snValue, auth)
    const metricUnitMapKey = `${realtimeKey}:metric-unit-map`
    const statusKey = `${realtimeKey}:status`

    if (!snValue || !hasAuth) {
      setSnapshot(previewFallback ? getPreviewEnergyDeviceSnapshot() : null)
      setLoading(false)
      return () => {
        cancelled = true
      }
    }

    const cachedSnapshot = getFreshSnapshotFromCache(realtimeKey)
    if (cachedSnapshot) {
      setSnapshot(cachedSnapshot)
      setLoading(false)
    } else {
      setLoading(true)
    }

    const pollMs = Math.max(3000, Number(options?.pollMs || 15000))

    const fetchState = async () => {
      try {
        const response = await queryStatusCached(auth, snValue, statusKey)
        if (cancelled) return
        const state = resolveStateFromStatus(response, snValue)
        if (!state) {
          setSnapshot(null)
          return
        }

        // Fast path: render immediately with cached unit map (or empty map).
        const fastUnitMap = getFreshMetricUnitMap(metricUnitMapKey) || {}
        const fastSnapshot = buildSnapshotFromState(state, fastUnitMap)
        setSnapshot(fastSnapshot)
        setSnapshotCache(realtimeKey, fastSnapshot)

        // Background enrichment: if unit map is not ready yet, fetch it and refresh once.
        if (!getFreshMetricUnitMap(metricUnitMapKey)) {
          loadMetricUnitMapCached(auth, snValue, metricUnitMapKey)
            .then((resolvedUnitMap) => {
              if (cancelled) return
              if (Object.keys(resolvedUnitMap).length === 0) return
              const enrichedSnapshot = buildSnapshotFromState(state, resolvedUnitMap)
              setSnapshot(enrichedSnapshot)
              setSnapshotCache(realtimeKey, enrichedSnapshot)
            })
            .catch(() => {
              // Keep fast snapshot if unit map lookup fails.
            })
        }
      } catch {
        if (cancelled) return
        const staleSnapshot = getFreshSnapshotFromCache(realtimeKey)
        setSnapshot(staleSnapshot ?? null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchState()
    const timer = window.setInterval(fetchState, pollMs)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [credentials?.accessToken, credentials?.apiKey, credentials?.appId, deviceSN, options?.pollMs, previewFallback])

  const hasPhaseData = useMemo(() => {
    if (!snapshot) return false
    return PHASES.some((phase) => snapshot.phases[phase].power !== null)
  }, [snapshot])

  return { snapshot, loading, hasPhaseData }
}
